import { useEffect, useRef, useState } from "react";
import { Globe, ChevronDown } from "lucide-react";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ms", label: "Bahasa Melayu" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "ar", label: "العربية" },
  { code: "zh-CN", label: "中文 (简体)" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "es", label: "Español" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "ru", label: "Русский" },
  { code: "th", label: "ไทย" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "tr", label: "Türkçe" },
  { code: "ur", label: "اردو" },
  { code: "bn", label: "বাংলা" },
];

let scriptLoaded = false;

function ensureScript() {
  if (scriptLoaded) return;
  window.googleTranslateElementInit = () => {
    // eslint-disable-next-line no-undef
    new window.google.translate.TranslateElement(
      { pageLanguage: "en", autoDisplay: false, layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE },
      "google_translate_hidden"
    );
  };
  const s = document.createElement("script");
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.head.appendChild(s);
  scriptLoaded = true;
}

function setCookie(name, val) {
  const host = window.location.hostname;
  const bases = ["", "." + host, "." + host.split(".").slice(-2).join(".")];
  bases.forEach((d) => {
    document.cookie = `${name}=${val}; path=/;${d ? ` domain=${d};` : ""}`;
  });
}

export default function GoogleTranslate({ dark = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("en");
  const wrapRef = useRef(null);

  useEffect(() => {
    ensureScript();
    // Restore existing selection from cookie
    const m = document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/);
    if (m) setCurrent(m[1]);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (code) => {
    setCurrent(code);
    setOpen(false);
    if (code === "en") {
      // Clear translation
      setCookie("googtrans", "");
      window.location.reload();
      return;
    }
    setCookie("googtrans", `/en/${code}`);
    window.location.reload();
  };

  const currentLabel = LANGS.find((l) => l.code === current)?.label || "English";

  return (
    <div className="relative" ref={wrapRef}>
      {/* Hidden mount point required by Google Translate */}
      <div id="google_translate_hidden" style={{ display: "none" }} />
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher"
        className={`inline-flex items-center gap-1.5 text-[12px] tracking-[0.08em] transition-colors ${
          dark ? "text-[#3b1a1a] hover:text-[#7c1d1d]" : "text-white hover:text-[#e8c970]"
        }`}
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{currentLabel}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          data-testid="lang-menu"
          className="absolute right-0 top-full mt-2 w-56 max-h-[70vh] overflow-y-auto bg-[#fdfbf7] border border-[#d4af37]/30 shadow-2xl z-50 notranslate"
          translate="no"
        >
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              data-testid={`lang-${l.code}`}
              className={`w-full text-left px-4 py-2 text-sm border-b border-[#d4af37]/10 hover:bg-[#faf7ee] transition-colors ${
                current === l.code ? "bg-[#14523d] text-white hover:bg-[#0f3d2c]" : "text-[#3b1a1a]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
